import React from 'react'
import axios from 'axios'
import * as yup from 'yup'
import { observer } from 'mobx-react'
import {
    FormGroup,
    BaseForm,
    Input,
    Fieldset,
} from '@softwaredev_cegeka/stix-form'
import { Grid, Pagination, Button, Icon } from '@softwaredev_cegeka/stix-core'

import { AuthApi } from 'src/api/authApi'
import FormLayout from 'src/components/Forms/FormLayout'
import AsyncSelect from 'src/components/Forms/AsyncSelect'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import TenantUsersTable from 'src/components/EntityTable/TenantUsersTable'
import { yupSelectRequired } from 'src/utils/validation'
import CompanySelect from 'src/components/CompanySelect'

import './tenantForm.scss'

class TenantFormContent extends BaseForm {
    initialValues = {
        name: '',
        organizationId: '',
        emailDomains: [],
        users: [],
        companies: [],
        parentTenant: null,
    }

    pages = [
        {
            title: 'Tenant form',
            renderMethod: this.renderPage,
            validationSchema: yup.object().shape({
                name: yup
                    .string()
                    .matches(/^[a-zA-Z0-9'.\-_ ]+$/, {
                        message:
                            "Please do not insert special characters. Only '.', ''', '-', '_', ' ' allowed.",
                        excludeEmptyString: true,
                    })
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please insert the name of the tenant')
                    .typeError('Please insert the name of the tenant'),
                organizationId: yup
                    .string()
                    .matches(/^[a-zA-Z0-9.-_]+$/, {
                        message:
                            "Please do not insert special characters. Only '.', '-', '_' allowed.",
                        excludeEmptyString: true,
                    })
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please insert the organization ID')
                    .typeError('Please insert the organization ID'),
                emailDomains: yup
                    .array()
                    .typeError('Please insert/select at least one email domain')
                    .of(
                        yupSelectRequired(
                            'Please insert/select at least one email domain'
                        )
                    ),
                users: yup.array().of(yup.string()).nullable(),
                companies: yup
                    .array()
                    .typeError('Please insert/select at least one company')
                    .of(
                        yupSelectRequired(
                            'Please insert/select at least one company'
                        )
                    ),
            }),
        },
    ]

    authClient = new AuthApi(authenticatedUserStore.authToken)

    constructor(props, context) {
        super(props, context)
        this.state.values = { ...this.initialValues }
        this.state = {
            ...this.state,
            tenantAdmins: [],
            page: 1,
            loading: false,
            totalPages: 1,
            params: {
                per_page: 5,
                user_is_admin: true,
            },
            addUserView: false,
        }
        if (props.match.params.id) {
            this.authClient
                .fetchTenant(props.match.params.id)
                .then(response => {
                    this.initialValues = {
                        name: response.data.name,
                        organizationId: response.data.organization_id,
                        emailDomains: response.data.email_domains.map(item => ({
                            id: item.id,
                            label: item.email_domain,
                            value: item.email_domain,
                        })),
                        companies: response.data.companies.map(item => ({
                            id: item.company_id,
                            label: item.company_name,
                            value: item.company_id,
                        })),
                        parentTenant: response.data.parent_tenant
                            ? {
                                  id: response.data.parent_tenant.id,
                                  label: response.data.parent_tenant.name,
                                  value: response.data.parent_tenant.id,
                              }
                            : {},
                    }
                    this.setState({
                        values: { ...this.initialValues },
                    })
                })
            this.fetchAndStoreUsers()
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    async generatePayload(values) {
        return {
            name: values.name,
            organization_id: values.organizationId,
            email_domains: values.emailDomains.map(item => item.value),
            company_ids: values.companies.map(item => item.value),
            user_admin_ids: [values.users?.id],
            parent_tenant_id: values.parentTenant?.id,
        }
    }

    // You can override this, or pass an onSubmit prop
    async submitData(payload) {
        // Submit data using axios
        let failedCalls = []
        let tenantCreated = false
        const editMode = !!this.props.match.params.id
        try {
            let resp = await axios({
                url: editMode
                    ? `/api/auth/tenants/${this.props.match.params.id}`
                    : '/api/auth/tenants',
                method: editMode ? 'PUT' : 'POST',
                data: {
                    name: payload.name,
                    organization_id: payload.organization_id,
                    company_ids: payload.company_ids,
                    parent_tenant_id: payload.parent_tenant_id,
                    user_admin_ids: payload.user_admin_ids,
                },
            })
            if (resp.status !== 200 && resp.status !== 204) {
                notifierStore.setToastInfo(
                    editMode
                        ? 'Tenant information could not be updated'
                        : 'Tenant could not be created with the data provided',
                    false
                )
            } else {
                tenantCreated = resp.data.id
            }
        } catch (ex) {
            console.warn('API Exception', ex)
            notifierStore.setToastInfo(
                ex.response?.data?.error ||
                    ex.response?.data?.detail ||
                    'Tenant could not be created/updated with the data provided',
                false
            )
            notifierStore.setShowMessage(true)
        }
        if (tenantCreated) {
            const initialDomains = this.initialValues.emailDomains.map(
                domainObj => domainObj.value
            )
            let domainsToBeAdded = payload.email_domains.filter(
                x => !initialDomains.includes(x)
            )
            let domainsToBeRemoved = initialDomains.filter(
                x => !payload.email_domains.includes(x)
            )
            await Promise.all(
                domainsToBeAdded.map(async domain => {
                    try {
                        let resp = await axios({
                            url: `/api/auth/tenants/${tenantCreated}/email_domains`,
                            method: 'POST',
                            data: {
                                email_domain: domain,
                            },
                        })
                        if (resp.status !== 200 && resp.status !== 204) {
                            failedCalls.push(domain)
                        }
                    } catch (ex) {
                        failedCalls.push(domain)
                    }
                })
            )
            await Promise.all(
                domainsToBeRemoved.map(async domain => {
                    const domainId = this.initialValues.emailDomains.filter(
                        item => item.value === domain
                    )[0].id
                    const tenantId = tenantCreated || this.props.match.params.id
                    try {
                        let resp = await axios({
                            url: `/api/auth/tenants/${tenantId}/email_domains/${domainId}`,
                            method: 'DELETE',
                        })
                        if (resp.status !== 200 && resp.status !== 204) {
                            failedCalls.push(domain)
                        }
                    } catch (ex) {
                        failedCalls.push(domain)
                    }
                })
            )

            if (failedCalls.length !== 0) {
                notifierStore.setToastInfo(
                    'Could not unlink the following email domains to the tenant: ' +
                        failedCalls,
                    false
                )
            } else {
                notifierStore.setToastInfo(
                    editMode
                        ? 'Successfully updated the tenant'
                        : 'Successfully created the tenant',
                    true
                )
            }
            notifierStore.setShowMessage(true)
            setTimeout(() => {
                this.props.history.push('/tenants')
            }, 2000)
        }
    }

    handleEmailDomainsRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.email_domain,
            label: item.email_domain,
        }))
    }

    handleCompanyTenantRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.id,
            label: item.name,
        }))
    }

    getNewOptionData(inputValue, optionLabel) {
        return {
            isNewOption: true,
            value: inputValue,
            label: optionLabel,
        }
    }

    formatCreateLabel(inputValue) {
        return `${inputValue} (new)`
    }

    fetchAndStoreUsers = (page = this.state.page) => {
        this.state = {
            ...this.state,
            loading: true,
        }
        this.authClient
            .fetchUsers(
                page,
                this.props.history,
                this.state.params,
                this.props.match.params.id
            )
            .then(response => {
                this.setState({
                    tenantAdmins: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    loading: false,
                })
            })
    }

    handlePageChange = page => {
        this.setState(
            {
                page: page,
            },
            () => this.fetchAndStoreUsers()
        )
    }

    setParams = params => {
        this.setState(
            {
                params: params,
            },
            () => this.fetchAndStoreUsers()
        )
    }

    enableAddUser = () => {
        this.setState({
            addUserView: true,
        })
    }

    handleUsersRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.first_name + ' ' + item.last_name,
            label: item.first_name + ' ' + item.last_name,
        }))
    }

    saveUserAsTenantAdmin = () => {
        this.authClient
            .setUserAsTenantAdmin(
                this.state.values.users.id,
                this.props.match.params.id
            )
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'User successfully set as tenant admin',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to set the user as tenant admin',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(() => {
                notifierStore.setShowMessage(true)
                this.setState({
                    addUserView: false,
                })
            })
    }

    removeUserAsTenantAdmin = userId => {
        this.authClient
            .removeUserAsTenantAdmin(userId, this.props.match.params.id)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'Tenant admin successfully unlinked from tenant',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to unlink tenant admin from the tenant',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    renderAddUserView = () => {
        return (
            <>
                <Grid.Row>
                    <Grid.Col lg={6}>
                        <FormGroup>
                            <AsyncSelect
                                {...this.getCommonSelectProps('users')}
                                apiConfig={{
                                    url: '/api/auth/users',
                                    params: inputValue => ({
                                        page: 1,
                                        first_name_like: inputValue,
                                        last_name_like: inputValue,
                                        sort: '+first_name',
                                        use_or: true,
                                    }),
                                }}
                                label={'Users'}
                                itemRenderer={this.handleUsersRender}
                                search
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col>
                        <FormGroup>
                            <Button
                                className={'mb-3'}
                                size={'sm'}
                                key="submit"
                                variant="secondary"
                                onClick={this.saveUserAsTenantAdmin}
                            >
                                Save user as tenant admin
                            </Button>
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
            </>
        )
    }

    addTenantAdmin = () => {
        return (
            <>
                <Grid.Row>
                    <Grid.Col lg={6}>
                        <FormGroup>
                            <AsyncSelect
                                {...this.getCommonSelectProps('users')}
                                apiConfig={{
                                    url: '/api/auth/users',
                                    params: inputValue => ({
                                        page: 1,
                                        first_name_like: inputValue,
                                        last_name_like: inputValue,
                                        sort: '+first_name',
                                        use_or: true,
                                    }),
                                }}
                                label={'Tenant admin'}
                                itemRenderer={this.handleUsersRender}
                                search
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
            </>
        )
    }

    async checkIfTenantAdminOrGlobalAdmin() {
        if (authenticatedUserStore.isGlobalAdmin) {
            return true
        }
        let allowed = false
        let response = await this.authClient.fetchUser(
            authenticatedUserStore.userData.username
        )
        const tenants = response?.data?.tenants || []
        for (let i = 0; i < tenants.length; i++) {
            if (
                tenants[i].id.toString() ===
                this.props.match.params.id?.toString()
            ) {
                allowed = true
            }
        }
        return allowed
    }

    renderPage() {
        const allowedToEdit = this.checkIfTenantAdminOrGlobalAdmin()
        return (
            <>
                <Fieldset title="General info">
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('name')}
                                    label="Name"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps(
                                        'organizationId'
                                    )}
                                    label="Organization ID"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col md={6}>
                            <FormGroup>
                                <AsyncSelect
                                    {...this.getCommonSelectProps(
                                        'emailDomains'
                                    )}
                                    getNewOptionData={this.getNewOptionData}
                                    formatCreateLabel={this.formatCreateLabel}
                                    comboBox={true}
                                    apiConfig={{
                                        url: '/api/auth/email_domains',
                                        params: inputValue => ({
                                            page: 1,
                                            email_domain_like: inputValue,
                                            sort: '+email_domain',
                                        }),
                                    }}
                                    required
                                    itemRenderer={this.handleEmailDomainsRender}
                                    label="Email domains"
                                    search
                                    isMulti
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col md={6}>
                            <FormGroup>
                                <CompanySelect
                                    {...this.getCommonSelectProps('companies')}
                                    required
                                    label="Companies"
                                    isMulti
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col md={6}>
                            <FormGroup>
                                <AsyncSelect
                                    {...this.getCommonSelectProps(
                                        'parentTenant'
                                    )}
                                    apiConfig={{
                                        url: '/api/auth/tenants',
                                        params: inputValue => ({
                                            page: 1,
                                            name_like: inputValue,
                                        }),
                                    }}
                                    isClearable
                                    itemRenderer={
                                        this.handleCompanyTenantRender
                                    }
                                    label="Parent Tenant"
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    {!this.props.match.params.id && this.addTenantAdmin()}
                </Fieldset>
                {this.props.match.params.id && allowedToEdit && (
                    <Fieldset title="Users">
                        {!this.state.addUserView && (
                            <Button
                                variant="naked"
                                onClick={this.enableAddUser}
                            >
                                <Icon icon="add" />
                                <span>Add tenant admin</span>
                            </Button>
                        )}
                        {this.state.addUserView && this.renderAddUserView()}
                        <div className="data-table-container">
                            <TenantUsersTable
                                {...this.props}
                                data={this.state.tenantAdmins}
                                loading={this.state.loading}
                                params={this.state.params}
                                setParams={this.setParams}
                                removeUserAsTenantAdmin={
                                    this.removeUserAsTenantAdmin
                                }
                            />

                            {this.state.totalPages >= 2 && (
                                <div className="pagination-wrapper">
                                    <Pagination
                                        lastPage={this.state.totalPages}
                                        currentPage={this.state.page}
                                        onClick={
                                            this.state.loading
                                                ? () => {}
                                                : this.handlePageChange
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </Fieldset>
                )}
            </>
        )
    }
}

class TenantForm extends React.Component {
    render() {
        return (
            <FormLayout {...this.props}>
                <TenantFormContent {...this.props} />
            </FormLayout>
        )
    }
}

export default observer(TenantForm)
